import { useState, useRef, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useActiveMunicipio } from '@/context/ActiveMunicipioContext';
import { useModalOverflow } from '@/hooks/useModalOverflow';
import { useModalAnimation } from '@/hooks/useModalAnimation';
import {
  ParsedRow,
  COLUMN_MAP,
  stripBOM,
  detectDelimiter,
  parseCSV,
  findColumnIndex,
  buildAutoColIdx,
  buildParsedRow,
} from './csv/csvUtils';
import MapeamentoStep from './csv/MapeamentoStep';
import DuplicadosStep, { DuplicadoInfo } from './csv/DuplicadosStep';
import EdicaoMassaStep from './csv/EdicaoMassaStep';

interface ImportarCsvModalProps {
  onClose: () => void;
  onSuccess: (count: number) => void;
}

// Steps do wizard
type Step = 'upload' | 'mapeamento' | 'preview' | 'duplicados' | 'edicao' | 'importing' | 'done' | 'error';

const STEP_LABELS: Record<Step, string> = {
  upload: 'Carregar ficheiro',
  mapeamento: 'Mapear colunas',
  preview: 'Pré-visualização',
  duplicados: 'Verificar duplicados',
  edicao: 'Edição em massa',
  importing: 'A importar',
  done: 'Concluído',
  error: 'Erro',
};

const WIZARD_STEPS: Step[] = ['upload', 'mapeamento', 'preview', 'duplicados', 'edicao'];

export default function ImportarCsvModal({ onClose, onSuccess }: ImportarCsvModalProps) {
  const { activeMunicipio } = useActiveMunicipio();
  const { animating, handleClose } = useModalAnimation(onClose);
  const [step, setStep] = useState<Step>('upload');
  const [dragOver, setDragOver] = useState(false);
  const [fileName, setFileName] = useState('');
  const [delimiter, setDelimiter] = useState(',');
  const [detectedDelimiter, setDetectedDelimiter] = useState('');
  const [rawHeaders, setRawHeaders] = useState<string[]>([]);
  const [rawRows, setRawRows] = useState<string[][]>([]);
  const [colIdx, setColIdx] = useState<Record<string, number>>({});
  const [parsedRows, setParsedRows] = useState<ParsedRow[]>([]);
  const [finalRows, setFinalRows] = useState<ParsedRow[]>([]);
  const [importProgress, setImportProgress] = useState(0);
  const [importErrors, setImportErrors] = useState<string[]>([]);
  const [importedCount, setImportedCount] = useState(0);
  const [errorMsg, setErrorMsg] = useState('');
  const [skipErrors, setSkipErrors] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const rawTextRef = useRef('');

  useModalOverflow();

  // ── Parsing ──────────────────────────────────────────────────────────────

  const buildRows = useCallback((headers: string[], dataRows: string[][], mapping: Record<string, number>): ParsedRow[] => {
    return dataRows
      .filter((row) => !row.every((c) => !c.trim()))
      .map((row, i) => buildParsedRow(row, headers, mapping, i));
  }, []);

  const processFile = useCallback((text: string, name: string) => {
    setFileName(name);
    const clean = stripBOM(text);
    rawTextRef.current = clean;
    const detected = detectDelimiter(clean);
    setDetectedDelimiter(detected);
    setDelimiter(detected);

    const rows = parseCSV(clean, detected);
    if (rows.length < 2) {
      setErrorMsg('O ficheiro está vazio ou tem apenas o cabeçalho.');
      setStep('error');
      return;
    }

    const headers = rows[0];
    const dataRows = rows.slice(1);
    setRawHeaders(headers);
    setRawRows(dataRows);

    const autoMapping = buildAutoColIdx(headers);
    setColIdx(autoMapping);

    // Verificar se há colunas não mapeadas automaticamente
    const unmappedCount = Object.values(autoMapping).filter((v) => v === -1).length;
    const parsed = buildRows(headers, dataRows, autoMapping);
    setParsedRows(parsed);

    // Se muitas colunas não foram mapeadas, ir para mapeamento manual
    if (unmappedCount > Object.keys(COLUMN_MAP).length / 2) {
      setStep('mapeamento');
    } else {
      setStep('preview');
    }
  }, [buildRows]);

  const handleFile = useCallback((file: File) => {
    if (!file.name.match(/\.(csv|txt|tsv)$/i)) {
      setErrorMsg('Formato não suportado. Usa um ficheiro .csv, .txt ou .tsv');
      setStep('error');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      processFile(text, file.name);
    };
    reader.readAsText(file, 'UTF-8');
  }, [processFile]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const handleDelimiterChange = useCallback((newDelimiter: string) => {
    setDelimiter(newDelimiter);
    const rows = parseCSV(rawTextRef.current, newDelimiter);
    if (rows.length < 2) return;
    const headers = rows[0];
    const dataRows = rows.slice(1);
    setRawHeaders(headers);
    setRawRows(dataRows);
    const autoMapping = buildAutoColIdx(headers);
    setColIdx(autoMapping);
    const parsed = buildRows(headers, dataRows, autoMapping);
    setParsedRows(parsed);
  }, [buildRows]);

  // ── Mapeamento confirmado ─────────────────────────────────────────────────

  const handleMapeamentoConfirm = useCallback((mapping: Record<string, number>) => {
    setColIdx(mapping);
    const parsed = buildRows(rawHeaders, rawRows, mapping);
    setParsedRows(parsed);
    setStep('preview');
  }, [rawHeaders, rawRows, buildRows]);

  // ── Duplicados confirmados ────────────────────────────────────────────────

  const handleDuplicadosConfirm = useCallback((duplicados: DuplicadoInfo[], novos: ParsedRow[]) => {
    const toUpdate = duplicados.filter((d) => d.action === 'atualizar').map((d) => d.row);
    const allRows = [...novos, ...toUpdate];
    setFinalRows(allRows);
    setStep('edicao');
  }, []);

  // ── Importação ────────────────────────────────────────────────────────────

  const handleImport = async (rowsToImport: ParsedRow[]) => {
    const validRows = rowsToImport.filter((r) => r.nome);
    if (validRows.length === 0) return;

    setStep('importing');
    setImportProgress(0);
    const errors: string[] = [];
    let imported = 0;
    const municipioId = activeMunicipio?.id ?? null;

    const BATCH = 20;
    for (let i = 0; i < validRows.length; i += BATCH) {
      const batch = validRows.slice(i, i + BATCH).map((r) => ({
        id: r.id,
        nome: r.nome,
        carreira: r.carreira,
        sexo: r.sexo,
        nivel_atual: r.nivel_atual,
        novo_nivel_2025: r.novo_nivel_2025,
        vencimento: r.vencimento,
        avaliacao: r.avaliacao,
        ultima_progressao: r.ultima_progressao,
        data_entrada_funcao_publica: r.data_entrada_funcao_publica,
        unidade_organica: r.unidade_organica,
        subunidade_organica: r.subunidade_organica,
        pontos_atuais: r.pontos_atuais,
        pontos_descontados: 0,
        excecao_prepav: r.excecao_prepav,
        descricao_subida: r.descricao_subida,
        email: r.email,
        telefone: r.telefone,
        data_nascimento: r.data_nascimento,
        cpf: r.cpf,
        endereco: r.endereco,
        status: r.status,
        superior_hierarquico: r.superior_hierarquico,
        categoria: r.categoria,
        despacho_autorizacao: r.despacho_autorizacao,
        certificados: r.certificados,
        municipio_id: municipioId,
        updated_at: new Date().toISOString(),
      }));

      const { error } = await supabase
        .from('funcionarios')
        .upsert(batch, { onConflict: 'id' });

      if (error) {
        errors.push(`Linhas ${i + 1}–${i + batch.length}: ${error.message}`);
      } else {
        imported += batch.length;
      }
      setImportProgress(Math.round(((i + BATCH) / validRows.length) * 100));
    }

    setImportErrors(errors);
    setImportedCount(imported);
    if (imported > 0) {
      setStep('done');
      onSuccess(imported);
    } else {
      setStep('error');
      setErrorMsg(errors[0] ?? 'Erro desconhecido durante a importação.');
    }
  };

  // ── Helpers ───────────────────────────────────────────────────────────────

  const rowsWithErrors = parsedRows.filter((r) => r._errors.length > 0);
  const rowsOk = parsedRows.filter((r) => r._errors.length === 0);
  const isSoftError = (e: string) => e.includes('gerado automaticamente');

  const stepIndex = WIZARD_STEPS.indexOf(step);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={handleClose}>
      <div className={`absolute inset-0 bg-black/50 backdrop-blur-sm ${animating ? 'modal-backdrop-active' : 'modal-backdrop-enter'}`} />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden ${animating ? 'modal-panel-active' : 'modal-panel-enter'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="px-6 py-4 border-b border-zinc-100 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 flex items-center justify-center bg-zinc-100 rounded-xl">
              <i className="ri-file-upload-line text-zinc-600 text-base" />
            </div>
            <div>
              <h2 className="text-[15px] font-bold text-zinc-900">Importar Funcionários via CSV</h2>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                {step !== 'upload' && step !== 'error' ? STEP_LABELS[step] : 'Suporta vírgula, ponto e vírgula e tabulação'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            {/* Wizard progress */}
            {stepIndex >= 0 && (
              <div className="hidden md:flex items-center gap-1.5">
                {WIZARD_STEPS.map((s, i) => (
                  <div key={s} className="flex items-center gap-1.5">
                    <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold transition-colors ${
                      i < stepIndex ? 'bg-emerald-500 text-white' :
                      i === stepIndex ? 'bg-zinc-900 text-white' :
                      'bg-zinc-100 text-zinc-400'
                    }`}>
                      {i < stepIndex ? <i className="ri-check-line text-[10px]" /> : i + 1}
                    </div>
                    {i < WIZARD_STEPS.length - 1 && (
                      <div className={`w-6 h-0.5 rounded-full ${i < stepIndex ? 'bg-emerald-300' : 'bg-zinc-100'}`} />
                    )}
                  </div>
                ))}
              </div>
            )}
            <button onClick={handleClose} className="w-8 h-8 flex items-center justify-center rounded-lg text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 transition-colors cursor-pointer">
              <i className="ri-close-line text-lg" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto flex-1">

          {/* ── STEP: Upload ── */}
          {(step === 'upload' || step === 'error') && (
            <div className="p-6 space-y-5">
              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef.current?.click()}
                className={`border-2 border-dashed rounded-2xl p-10 flex flex-col items-center gap-4 cursor-pointer transition-all ${
                  dragOver ? 'border-zinc-400 bg-zinc-50' : 'border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50/50'
                }`}
              >
                <div className="w-14 h-14 flex items-center justify-center bg-zinc-100 rounded-2xl">
                  <i className="ri-file-text-line text-zinc-400 text-2xl" />
                </div>
                <div className="text-center">
                  <p className="text-[14px] font-semibold text-zinc-700">Arrasta o ficheiro CSV aqui</p>
                  <p className="text-[12px] text-zinc-400 mt-1">ou clica para selecionar · .csv, .txt, .tsv</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt,.tsv"
                  className="hidden"
                  onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); }}
                />
              </div>

              {step === 'error' && errorMsg && (
                <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <i className="ri-error-warning-fill text-red-500 text-base flex-shrink-0 mt-0.5" />
                  <p className="text-[13px] text-red-700">{errorMsg}</p>
                </div>
              )}

              {/* Download template */}
              <div className="flex items-center justify-between bg-zinc-900 rounded-xl px-4 py-3">
                <div>
                  <p className="text-[13px] font-semibold text-white">Descarregar template CSV</p>
                  <p className="text-[11px] text-zinc-400 mt-0.5">Ficheiro com o cabeçalho correto para preencher</p>
                </div>
                <button
                  onClick={() => {
                    const header = 'id,nome,email,telefone,data_nascimento,cpf,endereco,status,superior_hierarquico,categoria,genero,despacho_autorizacao,certificados,carreira,sexo,nivel_atual,novo_nivel_2025,vencimento,avaliacao,unidade_organica,data_entrada_funcao_publica';
                    const example = 'F001,Maria Silva,maria@cm.pt,912345678,1985-03-15,123456789,Rua das Flores 10,Ativo,João Costa,Técnica Superior,F,,,Técnica Superior,F,10,11,1500,Bom,Recursos Humanos,2010-01-01';
                    const blob = new Blob(['\uFEFF' + header + '\n' + example], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a');
                    a.href = url; a.download = 'template-funcionarios.csv'; a.click();
                    URL.revokeObjectURL(url);
                  }}
                  className="whitespace-nowrap flex items-center gap-2 px-3 py-2 bg-white text-zinc-900 text-[12px] font-semibold rounded-lg hover:bg-zinc-100 transition-colors cursor-pointer"
                >
                  <i className="ri-download-line text-sm" />
                  Template.csv
                </button>
              </div>

              {/* Guia de colunas */}
              <div className="bg-zinc-50 rounded-xl p-4">
                <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-3">Colunas reconhecidas automaticamente</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                  {[
                    { col: 'id / nº', req: true }, { col: 'nome', req: true },
                    { col: 'carreira', req: false }, { col: 'sexo / genero', req: false },
                    { col: 'nivel_atual', req: false }, { col: 'vencimento / salario', req: false },
                    { col: 'avaliacao', req: false }, { col: 'email', req: false },
                    { col: 'telefone', req: false }, { col: 'data_nascimento', req: false },
                    { col: 'cpf / nif', req: false }, { col: 'endereco / morada', req: false },
                    { col: 'status / estado', req: false }, { col: 'categoria / cargo', req: false },
                    { col: 'superior_hierarquico', req: false }, { col: 'unidade_organica', req: false },
                    { col: 'data_entrada_funcao_publica', req: false }, { col: 'despacho_autorizacao', req: false },
                  ].map((item) => (
                    <div key={item.col} className="flex items-center gap-1.5">
                      <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${item.req ? 'bg-red-400' : 'bg-zinc-300'}`} />
                      <span className="text-[11px] font-mono text-zinc-600">{item.col}</span>
                      {item.req && <span className="text-[10px] text-red-400 font-semibold">obrig.</span>}
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-zinc-400 mt-3">
                  Datas aceites: DD/MM/AAAA, DD-MM-AAAA ou AAAA-MM-DD · Se as colunas não forem reconhecidas, podes mapeá-las manualmente no passo seguinte.
                </p>
              </div>
            </div>
          )}

          {/* ── STEP: Mapeamento manual ── */}
          {step === 'mapeamento' && (
            <MapeamentoStep
              rawHeaders={rawHeaders}
              initialMapping={colIdx}
              onConfirm={handleMapeamentoConfirm}
              onBack={() => setStep('upload')}
            />
          )}

          {/* ── STEP: Preview ── */}
          {step === 'preview' && (
            <div className="p-6 space-y-4">
              {/* Ficheiro + separador */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-emerald-50 rounded-lg">
                    <i className="ri-file-text-line text-emerald-600 text-sm" />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-zinc-800">{fileName}</p>
                    <p className="text-[11px] text-zinc-400">{parsedRows.length} linha(s) · {rawHeaders.length} coluna(s)</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <button
                    onClick={() => setStep('mapeamento')}
                    className="whitespace-nowrap flex items-center gap-1.5 text-[11px] text-zinc-500 hover:text-zinc-800 border border-zinc-200 px-3 py-1.5 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
                  >
                    <i className="ri-map-pin-line text-xs" />
                    Ajustar mapeamento
                  </button>
                  <div className="flex items-center gap-1 bg-zinc-100 rounded-lg p-1">
                    {[{ val: ',', label: 'Vírgula' }, { val: ';', label: 'Ponto e vírgula' }, { val: '\t', label: 'Tab' }].map((d) => (
                      <button
                        key={d.val}
                        onClick={() => handleDelimiterChange(d.val)}
                        className={`whitespace-nowrap px-2.5 py-1 text-[11px] font-medium rounded-md transition-all cursor-pointer ${delimiter === d.val ? 'bg-white text-zinc-800' : 'text-zinc-500 hover:text-zinc-700'}`}
                      >
                        {d.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Resumo */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-emerald-50 rounded-xl p-3 text-center">
                  <p className="text-[22px] font-bold text-emerald-700">{rowsOk.length}</p>
                  <p className="text-[11px] text-emerald-600 font-medium">Prontos para importar</p>
                </div>
                <div className={`rounded-xl p-3 text-center ${rowsWithErrors.length > 0 ? 'bg-amber-50' : 'bg-zinc-50'}`}>
                  <p className={`text-[22px] font-bold ${rowsWithErrors.length > 0 ? 'text-amber-700' : 'text-zinc-400'}`}>{rowsWithErrors.length}</p>
                  <p className={`text-[11px] font-medium ${rowsWithErrors.length > 0 ? 'text-amber-600' : 'text-zinc-400'}`}>Com avisos</p>
                </div>
                <div className="bg-zinc-50 rounded-xl p-3 text-center">
                  <p className="text-[22px] font-bold text-zinc-700">{rawHeaders.length}</p>
                  <p className="text-[11px] text-zinc-500 font-medium">Colunas detetadas</p>
                </div>
              </div>

              {/* Colunas detetadas */}
              <div className="bg-zinc-50 border border-zinc-100 rounded-xl p-3">
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-2">Colunas no ficheiro</p>
                <div className="flex flex-wrap gap-1.5">
                  {rawHeaders.map((h, i) => {
                    const isId = findColumnIndex([h], COLUMN_MAP.id) !== -1;
                    const isNome = findColumnIndex([h], COLUMN_MAP.nome) !== -1;
                    const isMapped = Object.values(colIdx).includes(i);
                    return (
                      <span key={i} className={`text-[10px] font-mono px-2 py-0.5 rounded-md ${
                        isId || isNome ? 'bg-emerald-100 text-emerald-700 font-semibold' :
                        isMapped ? 'bg-zinc-200 text-zinc-600' :
                        'bg-zinc-100 text-zinc-400'
                      }`}>
                        {h}{isId && ' ✓ id'}{isNome && ' ✓ nome'}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Erros */}
              {rowsWithErrors.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <p className="text-[12px] font-semibold text-amber-700 flex items-center gap-2">
                      <i className="ri-error-warning-line" />
                      {rowsWithErrors.length} linha(s) com avisos
                    </p>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={skipErrors} onChange={(e) => setSkipErrors(e.target.checked)} className="w-3.5 h-3.5 cursor-pointer" />
                      <span className="text-[11px] text-amber-700">Ignorar e importar só as válidas</span>
                    </label>
                  </div>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {rowsWithErrors.slice(0, 8).map((r, i) => (
                      <div key={i} className="flex items-start gap-2 text-[11px] text-amber-700">
                        <span className="font-mono bg-amber-100 px-1 rounded flex-shrink-0">#{r.id}</span>
                        <span>{r._errors.join(', ')}</span>
                      </div>
                    ))}
                    {rowsWithErrors.length > 8 && <p className="text-[11px] text-amber-500">... e mais {rowsWithErrors.length - 8}</p>}
                  </div>
                </div>
              )}

              {/* Pré-visualização */}
              <div>
                <p className="text-[11px] font-semibold text-zinc-400 uppercase tracking-widest mb-2">Pré-visualização (primeiras 5 linhas)</p>
                <div className="overflow-x-auto rounded-xl border border-zinc-100">
                  <table className="w-full text-[11px]">
                    <thead>
                      <tr className="bg-zinc-50 border-b border-zinc-100">
                        {['Estado', 'Nº', 'Nome', 'Carreira', 'Sexo', 'Nível', 'Vencimento', 'Email'].map((h) => (
                          <th key={h} className="px-3 py-2 text-left font-semibold text-zinc-400 whitespace-nowrap">{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {parsedRows.slice(0, 5).map((r, i) => (
                        <tr key={i} className={`border-b border-zinc-50 ${r._errors.length > 0 ? 'bg-amber-50/50' : ''}`}>
                          <td className="px-3 py-2">
                            {r._errors.length > 0
                              ? <span className="text-amber-500"><i className="ri-error-warning-line" /></span>
                              : <span className="text-emerald-500"><i className="ri-checkbox-circle-line" /></span>
                            }
                          </td>
                          <td className="px-3 py-2 font-mono text-zinc-500">{r.id || <span className="text-red-400">—</span>}</td>
                          <td className="px-3 py-2 font-medium text-zinc-700 whitespace-nowrap">{r.nome || <span className="text-red-400">—</span>}</td>
                          <td className="px-3 py-2 text-zinc-500 whitespace-nowrap">{r.carreira || <span className="text-amber-400">—</span>}</td>
                          <td className="px-3 py-2 text-zinc-500">{r.sexo}</td>
                          <td className="px-3 py-2 text-zinc-500">{r.nivel_atual || <span className="text-amber-400">—</span>}</td>
                          <td className="px-3 py-2 text-zinc-500">{r.vencimento > 0 ? `${r.vencimento}€` : <span className="text-amber-400">—</span>}</td>
                          <td className="px-3 py-2 text-zinc-400">{r.email || '—'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-between pt-1">
                <button
                  onClick={() => { setStep('upload'); setParsedRows([]); setFileName(''); }}
                  className="whitespace-nowrap flex items-center gap-2 text-[13px] text-zinc-500 hover:text-zinc-800 border border-zinc-200 px-4 py-2 rounded-lg hover:bg-zinc-50 transition-colors cursor-pointer"
                >
                  <i className="ri-arrow-left-line text-sm" />
                  Outro ficheiro
                </button>
                <button
                  onClick={() => setStep('duplicados')}
                  disabled={parsedRows.length === 0}
                  className="whitespace-nowrap flex items-center gap-2 px-5 py-2 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer disabled:opacity-50"
                >
                  Verificar duplicados
                  <i className="ri-arrow-right-line text-sm" />
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: Duplicados ── */}
          {step === 'duplicados' && (
            <DuplicadosStep
              rows={skipErrors ? rowsOk : parsedRows.filter((r) => r.nome && r._errors.every(isSoftError))}
              municipioId={activeMunicipio?.id ?? null}
              onConfirm={handleDuplicadosConfirm}
              onBack={() => setStep('preview')}
            />
          )}

          {/* ── STEP: Edição em massa ── */}
          {step === 'edicao' && (
            <EdicaoMassaStep
              rows={finalRows}
              onConfirm={handleImport}
              onBack={() => setStep('duplicados')}
            />
          )}

          {/* ── STEP: Importing ── */}
          {step === 'importing' && (
            <div className="p-10 flex flex-col items-center gap-5">
              <div className="w-16 h-16 flex items-center justify-center bg-zinc-100 rounded-2xl">
                <i className="ri-loader-4-line text-zinc-400 text-3xl animate-spin" />
              </div>
              <div className="text-center">
                <p className="text-[15px] font-semibold text-zinc-800">A importar funcionários...</p>
                <p className="text-[12px] text-zinc-400 mt-1">{Math.min(importProgress, 100)}% concluído</p>
              </div>
              <div className="w-full max-w-xs h-2 bg-zinc-100 rounded-full overflow-hidden">
                <div className="h-full bg-zinc-900 rounded-full transition-all duration-300" style={{ width: `${Math.min(importProgress, 100)}%` }} />
              </div>
            </div>
          )}

          {/* ── STEP: Done ── */}
          {step === 'done' && (
            <div className="p-10 flex flex-col items-center gap-5">
              <div className="w-16 h-16 flex items-center justify-center bg-emerald-100 rounded-2xl">
                <i className="ri-checkbox-circle-fill text-emerald-500 text-3xl" />
              </div>
              <div className="text-center">
                <p className="text-[16px] font-bold text-zinc-900">Importação concluída!</p>
                <p className="text-[13px] text-zinc-500 mt-1">{importedCount} funcionário(s) importados com sucesso</p>
              </div>
              {importErrors.length > 0 && (
                <div className="w-full bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-[12px] font-semibold text-amber-700 mb-2">{importErrors.length} erro(s) durante a importação:</p>
                  {importErrors.map((e, i) => <p key={i} className="text-[11px] text-amber-600">{e}</p>)}
                </div>
              )}
              <button
                onClick={onClose}
                className="whitespace-nowrap px-6 py-2.5 bg-zinc-900 text-white text-[13px] font-medium rounded-lg hover:bg-zinc-800 transition-colors cursor-pointer"
              >
                Fechar
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
